/**
 * `/api/license-leases` payloads.
 *
 * All five endpoints now read and write THIS system's database. Until the
 * leasing port they were split: two aggregate reads hit our own tables while
 * `leases/:owner`, `licenses/:owner/periods` and `request-new-lease` proxied the
 * FastAPI service `swarm-license-staking-api`, whose privileged `X-Api-Key`
 * could not reach a browser. That service is being decommissioned into this API
 * — see `docs/runbooks/license-leasing-port.md`.
 *
 * The record shapes below are still the ones that service emitted, field for
 * field, because the browser clients were not changed by the port. Almost every
 * field is optional, and that is reproduced rather than tightened: the upstream
 * built these out of a SQLModel row whose columns are nullable, and declaring a
 * field required that a row may not carry turns a legitimate lease into a 500.
 *
 * ONE SHAPE WAS CORRECTED. `license_ids` is an ARRAY on the lease list and the
 * creation response — that is what the FastAPI service actually put on the wire,
 * and what `LicenseLeases.tsx` counts with `.length` — but the upstream's
 * generated OpenAPI client declared it a `string`, because the Python response
 * model inherited a table class whose validation SQLModel disables. That wrong
 * declaration was copied into this file during the earlier port, so
 * `apiRequest()` would have thrown `ApiError` on every real reply. It is an
 * array here. Inside `license_leases_by_duration_period` it genuinely IS a
 * comma-joined string, because the upstream joined it explicitly before
 * grouping; that variant is `periodLeaseRecordSchema`.
 */
import { z } from "zod";

import { signedRequestEnvelopeSchema, suiAddressSchema } from "../primitives";

// Declared in `leasing/constants.ts` with the rest of the programme's fixed
// parameters, re-exported here so `@swarm/shared/schemas` keeps serving it to
// the callers that already import it from this subpath.
export { LEASE_DURATION_MONTHS } from "../../leasing/constants";

/** Path parameter shared by the per-owner endpoints. */
export const leaseOwnerParamsSchema = z.object({
  owner: suiAddressSchema,
});

/** Resolved owner path parameter. */
export type LeaseOwnerParams = z.infer<typeof leaseOwnerParamsSchema>;

/**
 * Query for `GET /api/license-leases/daily-rewards/:owner`.
 *
 * `days` is clamped in-schema. It reaches an INTERVAL literal, where a NaN
 * produced a 500 and a huge value produced an interval overflow.
 */
export const leaseDailyRewardsQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(365).default(30),
});

/** Resolved daily-rewards query. */
export type LeaseDailyRewardsQuery = z.infer<typeof leaseDailyRewardsQuerySchema>;

/** One day of leasing distributions, with the running total carried forward. */
export const leaseDailyRewardPointSchema = z.object({
  /** Calendar day, `YYYY-MM-DD`. */
  date: z.string(),
  daily_reward: z.number(),
  /** Cumulative total including everything before the requested window. */
  accumulative_reward: z.number(),
});

/** Resolved daily-reward point. */
export type LeaseDailyRewardPoint = z.infer<typeof leaseDailyRewardPointSchema>;

/** Response for `GET /api/license-leases/daily-rewards/:owner`. */
export const leaseDailyRewardsResponseSchema = z.object({
  data: z.array(leaseDailyRewardPointSchema),
});

/** Resolved daily-rewards response. */
export type LeaseDailyRewardsResponse = z.infer<typeof leaseDailyRewardsResponseSchema>;

/**
 * Response for `GET /api/license-leases/network-stats`.
 *
 * Projected $TRUTH a single license would earn over each term at the *current*
 * pool occupancy. Keys are the duration in months as strings because that is how
 * the lease picker indexes them.
 */
export const leaseNetworkStatsResponseSchema = z.object({
  projected_returns_per_license: z.object({
    "6": z.number(),
    "12": z.number(),
    "18": z.number(),
  }),
});

/** Resolved network-stats response. */
export type LeaseNetworkStatsResponse = z.infer<typeof leaseNetworkStatsResponseSchema>;

/**
 * One lease as every read endpoint returns it.
 *
 * The three computed fields at the bottom are not columns: `days_remaining` and
 * `is_claimable` come from `apps/api/src/lib/lease-semantics.ts`, and
 * `claimed_at` is the RESOLVED release marker (the lease's own value, falling
 * back to a settled `daily_rewards_claims` row) rather than the raw column.
 */
export const leaseRecordSchema = z.object({
  lease_id: z.number().int().nullable().optional(),
  owner: z.string().optional(),
  /** On-chain object ids locked by this lease. */
  license_ids: z.array(z.string()).optional(),
  duration_months: z.number().int().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  /** Set when the lease's reward claim settled — this is what releases licenses. */
  claimed_at: z.string().nullable().optional(),
  status: z.string().optional(),
  accumulated_leasing_rewards: z.number().optional(),
  leasing_rewards_estimate: z.number().optional(),
  current_leasing_weight: z.number().optional(),
  created_at: z.string().nullable().optional(),
  days_remaining: z.number().nullable().optional(),
  is_claimable: z.boolean().optional(),
  daily_rewards_claims_id: z.number().int().nullable().optional(),
  /**
   * The lease's most recent `daily_leasing_distributions` row: its amount and
   * its `distribution_date` (`YYYY-MM-DD`, a UTC day). Null when nothing has
   * been distributed yet. The nightly job writes a day's row at 00:00 UTC and
   * `accumulated_leasing_rewards` already includes it, so the dashboard ticker
   * counts THIS amount up across THAT day instead of guessing a daily rate.
   * Additive: `/api/v1` callers that ignore unknown keys are unaffected.
   */
  last_daily_leasing_reward: z.number().nullable().optional(),
  last_distribution_date: z.string().nullable().optional(),
});

/** Resolved lease record. */
export type LeaseRecord = z.infer<typeof leaseRecordSchema>;

/**
 * The same lease inside `license_leases_by_duration_period`, where
 * `license_ids` is comma-joined into one string.
 *
 * Faithful to the upstream, which joined the array before grouping it. Not
 * unified with {@link leaseRecordSchema}: a union would let either form appear on
 * either endpoint, and a client doing `.length` cannot tell a two-license lease
 * from a 133-character string.
 */
export const periodLeaseRecordSchema = leaseRecordSchema.extend({
  license_ids: z.string().optional(),
});

/** Resolved period-grouped lease record. */
export type PeriodLeaseRecord = z.infer<typeof periodLeaseRecordSchema>;

/** Response for `GET /api/license-leases/leases/:owner`. */
export const getLeasesResponseSchema = z.object({
  leases: z.array(leaseRecordSchema),
  total_count: z.number().int(),
});

/** Resolved leases response. */
export type GetLeasesResponse = z.infer<typeof getLeasesResponseSchema>;

/**
 * One license as the periods endpoint returns it.
 *
 * Exactly the `licenses` table's columns and nothing else. The upstream computed
 * `is_locked` / `lease_id` / `locked_at` alongside each row and then dropped them
 * on the way out, because its response model listed only the columns; the lock
 * state reaches the client through `license_leases_by_duration_period` instead.
 */
export const licenseRecordSchema = z.object({
  id: z.number().int().nullable().optional(),
  license_id: z.string().optional(),
  license_type: z.string().nullable().optional(),
  referral_code: z.string().nullable().optional(),
  owner: z.string().nullable().optional(),
  current_price: z.string().nullable().optional(),
  referrer_id: z.string().nullable().optional(),
  referrer_wallet_address: z.string().nullable().optional(),
  sender: z.string().nullable().optional(),
  timestamp: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
});

/** Resolved license record. */
export type LicenseRecord = z.infer<typeof licenseRecordSchema>;

/**
 * Response for `GET /api/license-leases/licenses/:owner/periods`.
 *
 * `license_leases_by_duration_period` is keyed `"<start>_<end>"` with both dates
 * as `YYYY-MM-DD` — a lease TERM, not a duration in months. Every lease that
 * still holds its licenses appears under its own term key.
 */
export const licensesByPeriodResponseSchema = z.object({
  licenses: z.array(licenseRecordSchema),
  unleased_licenses: z.array(licenseRecordSchema),
  /** Keyed `"YYYY-MM-DD_YYYY-MM-DD"`: the lease's start and end day. */
  license_leases_by_duration_period: z.record(z.string(), z.array(periodLeaseRecordSchema)),
  total_licenses: z.number().int(),
  total_periods: z.number().int(),
});

/** Resolved licenses-by-period response. */
export type LicensesByPeriodResponse = z.infer<typeof licensesByPeriodResponseSchema>;

/**
 * Body for `POST /api/license-leases/request-new-lease`.
 *
 * `owner`, `quantity` and `duration_months` are the lease contract; the
 * signature envelope proves the wallet asked for exactly those and is consumed
 * here. Locking licenses is irreversible for the whole term, so the signed text
 * has to bind the quantity and the duration — verifying the signature alone
 * would let a message captured over unrelated text be replayed with an
 * attacker-chosen term.
 */
export const requestNewLeaseRequestSchema = signedRequestEnvelopeSchema.extend({
  owner: suiAddressSchema,
  quantity: z.number().int().positive(),
  duration_months: z.union([z.literal(6), z.literal(12), z.literal(18)]),
});

/** Resolved lease-creation body. */
export type RequestNewLeaseRequest = z.infer<typeof requestNewLeaseRequestSchema>;

/** Response for `POST /api/license-leases/request-new-lease`: the created lease. */
export const createLeaseResponseSchema = leaseRecordSchema;

/** Resolved lease-creation response. */
export type CreateLeaseResponse = z.infer<typeof createLeaseResponseSchema>;
